import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { getClientes, addCliente, updateCliente, deleteCliente, checkCPFExists as checkCPFExistsService } from '../services/firestoreService';
import { Cliente, ClienteFormData, ApiResponse } from '../types';
import { showToast, toastMessages } from '../utils/toast';

export const useClientes = (user: User | null) => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const loadClientes = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const result = await getClientes(user.uid);
      if (result.success && result.data) {
        setClientes(result.data);
      } else {
        setError(result.error || 'Erro ao carregar clientes');
        showToast.error(toastMessages.cliente.error);
      }
    } catch (error) {
      setError('Erro ao carregar clientes');
      showToast.error(toastMessages.cliente.error);
    } finally {
      setLoading(false);
    }
  };

  const createCliente = async (clienteData: ClienteFormData): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const clienteWithUser = {
        ...clienteData,
        userId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      const result = await addCliente(clienteWithUser);
      if (result.success) {
        await loadClientes();
        showToast.success(toastMessages.cliente.created);
        return true;
      } else {
        showToast.error(result.error || toastMessages.cliente.error);
        return false;
      }
    } catch (error) {
      showToast.error(toastMessages.cliente.error);
      return false;
    }
  };

  const editCliente = async (id: string, clienteData: ClienteFormData): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const clienteWithUser = {
        ...clienteData,
        userId: user.uid,
        updatedAt: new Date(),
      };
      
      const result = await updateCliente(id, clienteWithUser);
      if (result.success) {
        await loadClientes();
        showToast.success(toastMessages.cliente.updated);
        return true;
      } else {
        showToast.error(result.error || toastMessages.cliente.error);
        return false;
      }
    } catch (error) {
      showToast.error(toastMessages.cliente.error);
      return false;
    }
  };

  const removeCliente = async (id: string): Promise<boolean> => {
    try {
      const result = await deleteCliente(id);
      if (result.success) {
        setClientes(prev => prev.filter(c => c.id !== id));
        showToast.success(toastMessages.cliente.deleted);
        return true;
      } else {
        showToast.error(result.error || toastMessages.cliente.error);
        return false;
      }
    } catch (error) {
      showToast.error(toastMessages.cliente.error);
      return false;
    }
  };

  const searchClientes = (searchTerm: string): Cliente[] => {
    if (!searchTerm.trim()) return clientes;
    
    const term = searchTerm.toLowerCase();
    return clientes.filter(cliente =>
      cliente.nome.toLowerCase().includes(term) ||
      cliente.telefone.includes(term) ||
      cliente.cpf.includes(term) ||
      (cliente.medidas && cliente.medidas.toLowerCase().includes(term))
    );
  };

  // Função para verificar se CPF já existe (para validação em tempo real)
  const checkCPFExists = async (cpf: string, ignoreId?: string): Promise<boolean> => {
    try {
      return await checkCPFExistsService(cpf, ignoreId);
    } catch (error) {
      console.error('Erro ao verificar CPF:', error);
      return false;
    }
  };

  useEffect(() => {
    loadClientes();
  }, [user]);

  return {
    clientes,
    loading,
    error,
    createCliente,
    editCliente,
    removeCliente,
    searchClientes,
    checkCPFExists,
    refreshClientes: loadClientes,
  };
};
